import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LOGO, NavBar, FB_URL, IG_URL, WHATSAPP, RECEPTION_WHATSAPP, BANK, ADDRESS } from "../shared";


// ─── CONFIG ────────────────────────────────────────────────────────────────────
const BANK_DETAILS = BANK;

const ROOMS = [
  {
    id: "standard",
    name: "Standard Deluxe Single",
    price: 30000,
    emoji: "🛏️",
    color: "#c8860a",
    desc: "Comfortable single room with all essentials",
    features: ["Single bed", "En-suite bathroom", "Air conditioning", "TV", "WiFi", "Work desk"],
  },
  {
    id: "super",
    name: "Super Deluxe Double",
    price: 35000,
    emoji: "🛎️",
    color: "#3aad72",
    desc: "Spacious double room with premium furnishing",
    features: ["Double bed", "En-suite bathroom", "Air conditioning", "Smart TV", "WiFi", "Mini bar", "Sitting area"],
  },
  {
    id: "executive",
    name: "Executive Deluxe",
    price: 40000,
    emoji: "👑",
    color: "#9b7fd4",
    desc: "Our finest room — elegant, spacious, premium",
    features: ["King-size bed", "Luxury bathroom", "Air conditioning", "Smart TV", "WiFi", "Mini bar", "Lounge area", "Room service priority"],
  },
];

const BOOKING_CHANNELS = [
  { name: "Expedia", url: "https://expedia.com", icon: "✈️", color: "#e8960a" },
  { name: "Booking.com", url: "https://booking.com", icon: "🌍", color: "#003580" },
  { name: "Hotels.ng", url: "https://hotels.ng", icon: "🏨", color: "#e03a2e" },
];

const fmt = (n) => "₦" + Number(n).toLocaleString();

function diffDays(a, b) {
  if (!a || !b) return 0;
  const d = (new Date(b) - new Date(a)) / (1000 * 60 * 60 * 24);
  return d > 0 ? d : 0;
}

function buildWhatsApp({ bookingRef, room, checkin, checkout, nights, guests, breakfast, breakfastPrice, totalAmount, form }) {
  const bkfstLine = breakfast
    ? `\n  Breakfast: ${guests} guest(s) x ${fmt(breakfastPrice)}/night x ${nights} night(s) = ${fmt(breakfastPrice * guests * nights)}`
    : "\n  Breakfast: Not included";

  return `🏨 *NEW ROOM BOOKING — THE 401 HOTEL & SUITES LTD*
━━━━━━━━━━━━━━━━━━
🔖 Booking Ref: *${bookingRef}*
📅 Date: ${new Date().toLocaleDateString("en-NG", { weekday: "short", day: "numeric", month: "long", year: "numeric" })}
━━━━━━━━━━━━━━━━━━
🛏️ *ROOM DETAILS:*
  Room Type: ${room.name}
  Check-in: ${new Date(checkin).toLocaleDateString("en-NG", { weekday: "short", day: "numeric", month: "long", year: "numeric" })}
  Check-out: ${new Date(checkout).toLocaleDateString("en-NG", { weekday: "short", day: "numeric", month: "long", year: "numeric" })}
  Duration: ${nights} night(s)
  Guests: ${guests}${bkfstLine}
━━━━━━━━━━━━━━━━━━
💰 *PAYMENT BREAKDOWN:*
  Room: ${fmt(room.price)} x ${nights} night(s) = ${fmt(room.price * nights)}${breakfast ? `\n  Breakfast: ${fmt(breakfastPrice * guests * nights)}` : ""}
  *TOTAL: ${fmt(totalAmount)}*
  Payment: ${form.payment}
━━━━━━━━━━━━━━━━━━
👤 *GUEST DETAILS:*
  Name: ${form.name}
  Phone: ${form.phone}
  Email: ${form.email || "Not provided"}
  Special Requests: ${form.special || "None"}
━━━━━━━━━━━━━━━━━━
⚠️ Full payment required to confirm booking.
🔄 Full refund if cancelled within 48hrs.
Please reply CONFIRMED once payment is received.`;
}

export default function RoomBooking() {
  const navigate = useNavigate();
  const [page, setPage] = useState("home"); // home, rooms, detail, booking, confirm
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [checkin, setCheckin] = useState("");
  const [checkout, setCheckout] = useState("");
  const [guests, setGuests] = useState(1);
  const [breakfast, setBreakfast] = useState(false);
  const [breakfastPrice, setBreakfastPrice] = useState(5000);
  const [form, setForm] = useState({ name: "", phone: "", email: "", payment: "", special: "" });
  const [sending, setSending] = useState(false);
  const [bookingRef] = useState(() => "401R-" + Math.floor(1000 + Math.random() * 9000));

  const nights = diffDays(checkin, checkout);
  const roomTotal = selectedRoom ? selectedRoom.price * nights : 0;
  const bkfstTotal = breakfast ? breakfastPrice * guests * nights : 0;
  const grandTotal = roomTotal + bkfstTotal;

  const today = new Date().toISOString().split("T")[0];

  const sendBooking = () => {
    setSending(true);
    const msg = buildWhatsApp({ bookingRef, room: selectedRoom, checkin, checkout, nights, guests, breakfast, breakfastPrice, totalAmount: grandTotal, form });
    setTimeout(() => {
      window.open(`https://wa.me/${RECEPTION_WHATSAPP}?text=${encodeURIComponent(msg)}`, "_blank");
      setSending(false);
      setPage("confirm");
    }, 800);
  };

  // ── Colors ──────────────────────────────────────────────────────────────────
  const C = {
    bg: "#06080e", surface: "#0b0f18", card: "#111828", border: "#1e2a3a",
    gold: "#c8a040", goldLight: "#e8c060", cream: "#e8dfc8",
    muted: "#6a7a90", blue: "#3a6fd4", green: "#3aad72", purple: "#9b7fd4",
  };

  const goldBtn = { display: "block", width: "100%", background: `linear-gradient(135deg,${C.gold},${C.goldLight})`, border: "none", borderRadius: 12, padding: "15px", fontSize: 15, fontWeight: "bold", color: "#06080e", cursor: "pointer", letterSpacing: 1, fontFamily: "inherit", marginTop: 16 };
  const inp = { width: "100%", background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 14px", color: C.cream, fontSize: 14, fontFamily: "inherit", marginBottom: 14, boxSizing: "border-box", outline: "none", colorScheme: "dark" };
  const lbl = { display: "block", fontSize: 10, letterSpacing: 2, color: C.gold, marginBottom: 6 };

  // ── HOME ────────────────────────────────────────────────────────────────────
  const renderHome = () => (
    <div>
      {/* Hero */}
      <div style={{ background: `linear-gradient(160deg, #0d1526 0%, ${C.bg} 100%)`, padding: "40px 22px 32px", textAlign: "center", borderBottom: `1px solid ${C.border}` }}>
        <div style={{ display: "inline-block", border: `1px solid ${C.gold}55`, color: C.gold, padding: "3px 16px", borderRadius: 20, fontSize: 10, letterSpacing: 3, marginBottom: 18 }}>THE 401 HOTEL & SUITES LTD · AWKA</div>
        <h1 style={{ fontSize: 34, fontWeight: "bold", color: C.cream, margin: "0 0 8px", lineHeight: 1.1, letterSpacing: 1 }}>Book Your<br />Stay With Us</h1>
        <p style={{ color: C.muted, fontSize: 13, margin: "0 0 28px", letterSpacing: 1 }}>Quiet. Private. Professional. Serenity.</p>
        <button style={goldBtn} onClick={() => setPage("rooms")}>Check Available Rooms →</button>
      </div>

      {/* Book on other platforms */}
      <div style={{ padding: "20px" }}>
        <div style={{ fontSize: 10, letterSpacing: 3, color: C.gold, marginBottom: 14 }}>ALSO AVAILABLE ON</div>
        <div style={{ display: "flex", gap: 10 }}>
          {BOOKING_CHANNELS.map((ch) => (
            <a key={ch.name} href={ch.url} target="_blank" rel="noreferrer"
              style={{ flex: 1, background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "12px 8px", textAlign: "center", textDecoration: "none" }}>
              <div style={{ fontSize: 22, marginBottom: 6 }}>{ch.icon}</div>
              <div style={{ fontSize: 11, color: C.cream, fontWeight: "bold" }}>{ch.name}</div>
            </a>
          ))}
        </div>
      </div>

      {/* Social & Address Footer */}
      <div style={{ padding: "16px 20px 24px", borderTop: `1px solid ${C.border}`, textAlign: "center" }}>
        <div style={{ display: "flex", justifyContent: "center", gap: 16, marginBottom: 12 }}>
          <a href="https://instagram.com/the401hotel" target="_blank" rel="noreferrer"
            style={{ display: "flex", alignItems: "center", gap: 6, color: C.gold, fontSize: 12, textDecoration: "none", fontFamily: "inherit" }}>
            <span style={{ fontSize: 16 }}>📸</span> @the401hotel
          </a>
          <a href="https://www.facebook.com/share/1EJKkvzsNL/" target="_blank" rel="noreferrer"
            style={{ display: "flex", alignItems: "center", gap: 6, color: C.gold, fontSize: 12, textDecoration: "none", fontFamily: "inherit" }}>
            <span style={{ fontSize: 16 }}>📘</span> The 401 Hotel
          </a>
        </div>
        <p style={{ color: C.muted, fontSize: 10, letterSpacing: 1, lineHeight: 1.8, margin: 0 }}>
          Plot A/39 Iyiagu Estate, Off Abakaliki Street<br />Awka, Anambra State · the401homes.com
        </p>
      </div>
    </div>
  );
  const renderRooms = () => (
    <div style={{ padding: "20px 18px" }}>
      <div style={{ fontSize: 10, letterSpacing: 3, color: C.gold, marginBottom: 18 }}>SELECT YOUR ROOM</div>
      {ROOMS.map((room) => (
        <div key={room.id}
          onClick={() => { setSelectedRoom(room); setPage("detail"); }}
          style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 18, marginBottom: 16, overflow: "hidden", cursor: "pointer" }}>
          {/* Room color band */}
          <div style={{ background: `linear-gradient(135deg, ${room.color}33, ${room.color}11)`, borderBottom: `1px solid ${room.color}33`, padding: "20px 20px 16px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <div style={{ fontSize: 28, marginBottom: 8 }}>{room.emoji}</div>
                <div style={{ fontSize: 16, fontWeight: "bold", color: C.cream, marginBottom: 4 }}>{room.name}</div>
                <div style={{ fontSize: 12, color: C.muted }}>{room.desc}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 22, fontWeight: "bold", color: room.color }}>{fmt(room.price)}</div>
                <div style={{ fontSize: 10, color: C.muted, letterSpacing: 1 }}>PER NIGHT</div>

              </div>
            </div>
          </div>
          {/* Features */}
          <div style={{ padding: "14px 20px", display: "flex", flexWrap: "wrap", gap: 8 }}>
            {room.features.map((f) => (
              <span key={f} style={{ fontSize: 11, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6, padding: "3px 8px", color: C.muted }}>✓ {f}</span>
            ))}
          </div>
          <div style={{ padding: "0 20px 16px" }}>
            <div style={{ background: room.color, borderRadius: 10, padding: "10px", textAlign: "center", color: "#06080e", fontWeight: "bold", fontSize: 13 }}>
              Select This Room →
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  // ── ROOM DETAIL + DATES ─────────────────────────────────────────────────────
  const renderDetail = () => {
    const room = selectedRoom;
    return (
      <div style={{ padding: "20px 18px" }}>
        {/* Room summary */}
        <div style={{ background: `linear-gradient(135deg, ${room.color}22, ${C.card})`, border: `1px solid ${room.color}33`, borderRadius: 16, padding: "20px", marginBottom: 20 }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>{room.emoji}</div>
          <div style={{ fontSize: 18, fontWeight: "bold", color: C.cream }}>{room.name}</div>
          <div style={{ fontSize: 22, fontWeight: "bold", color: room.color, marginTop: 4 }}>{fmt(room.price)} <span style={{ fontSize: 12, color: C.muted, fontWeight: "normal" }}>/ night</span></div>
        </div>

        {/* Dates */}
        <div style={{ fontSize: 10, letterSpacing: 3, color: C.gold, marginBottom: 16 }}>SELECT DATES & GUESTS</div>
        <label style={lbl}>CHECK-IN DATE</label>
        <input type="date" style={inp} min={today} value={checkin} onChange={(e) => setCheckin(e.target.value)} />
        <label style={lbl}>CHECK-OUT DATE</label>
        <input type="date" style={inp} min={checkin || today} value={checkout} onChange={(e) => setCheckout(e.target.value)} />

        {/* Guests */}
        <label style={lbl}>NUMBER OF GUESTS</label>
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16 }}>
          <button onClick={() => setGuests(Math.max(1, guests - 1))} style={{ width: 36, height: 36, borderRadius: 8, border: `1px solid ${C.border}`, background: C.card, color: C.gold, fontSize: 20, cursor: "pointer" }}>−</button>
          <span style={{ fontSize: 20, fontWeight: "bold", color: C.cream, minWidth: 24, textAlign: "center" }}>{guests}</span>
          <button onClick={() => setGuests(Math.min(4, guests + 1))} style={{ width: 36, height: 36, borderRadius: 8, border: "none", background: C.gold, color: "#06080e", fontSize: 20, cursor: "pointer" }}>+</button>
          <span style={{ fontSize: 12, color: C.muted }}>guest{guests > 1 ? "s" : ""}</span>
        </div>

        {/* Breakfast toggle */}
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: "16px", marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: breakfast ? 14 : 0 }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: "bold", color: C.cream }}>🍳 Add Breakfast</div>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>Per person, per night</div>
            </div>
            <button onClick={() => setBreakfast(!breakfast)}
              style={{ background: breakfast ? C.green : C.surface, border: `1px solid ${breakfast ? C.green : C.border}`, borderRadius: 20, padding: "6px 16px", color: breakfast ? "#fff" : C.muted, cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: "bold" }}>
              {breakfast ? "✓ Added" : "+ Add"}
            </button>
          </div>
          {breakfast && (
            <div>
              <label style={{ ...lbl, marginBottom: 8 }}>BREAKFAST PRICE PER PERSON PER NIGHT (₦)</label>
              <input type="number" style={{ ...inp, marginBottom: 0 }} value={breakfastPrice}
                onChange={(e) => setBreakfastPrice(Number(e.target.value))}
                placeholder="e.g. 5000" min="0" step="500" />
            </div>
          )}
        </div>

        {/* Price summary */}
        {nights > 0 && (
          <div style={{ background: C.card, border: `1px solid ${room.color}33`, borderRadius: 14, padding: "16px", marginBottom: 16 }}>
            <div style={{ fontSize: 10, letterSpacing: 3, color: C.gold, marginBottom: 12 }}>PRICE SUMMARY</div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 8 }}>
              <span style={{ color: C.muted }}>Room ({nights} night{nights > 1 ? "s" : ""})</span>
              <span style={{ color: C.cream }}>{fmt(room.price * nights)}</span>
            </div>
            {breakfast && (
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 8 }}>
                <span style={{ color: C.muted }}>Breakfast ({guests} guest{guests > 1 ? "s" : ""} × {nights} night{nights > 1 ? "s" : ""})</span>
                <span style={{ color: C.cream }}>{fmt(bkfstTotal)}</span>
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "space-between", borderTop: `1px solid ${C.border}`, paddingTop: 10, marginTop: 4 }}>
              <span style={{ fontWeight: "bold", fontSize: 15 }}>Total</span>
              <span style={{ fontWeight: "bold", fontSize: 20, color: room.color }}>{fmt(grandTotal)}</span>
            </div>
          </div>
        )}

        <button style={{ ...goldBtn, background: nights > 0 ? `linear-gradient(135deg,${C.gold},${C.goldLight})` : C.surface, color: nights > 0 ? "#06080e" : C.muted, cursor: nights > 0 ? "pointer" : "not-allowed" }}
          onClick={() => { if (nights > 0) setPage("booking"); else alert("Please select your check-in and check-out dates."); }}>
          {nights > 0 ? `Continue — ${fmt(grandTotal)} total →` : "Select dates to continue"}
        </button>
      </div>
    );
  };

  // ── BOOKING FORM ────────────────────────────────────────────────────────────
  const renderBooking = () => {
    const payOpts = ["Bank Transfer (Zenith Bank)", "POS on Arrival", "Pay via Booking.com", "Pay via Expedia"];
    return (
      <div style={{ padding: "20px 18px" }}>
        <div style={{ fontSize: 10, letterSpacing: 3, color: C.gold, marginBottom: 18 }}>YOUR DETAILS</div>

        <label style={lbl}>FULL NAME</label>
        <input style={inp} placeholder="As on your ID" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />

        <label style={lbl}>PHONE NUMBER</label>
        <input style={inp} placeholder="08XXXXXXXXX" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />

        <label style={lbl}>EMAIL ADDRESS (OPTIONAL)</label>
        <input style={inp} placeholder="your@email.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />

        <label style={lbl}>PAYMENT METHOD</label>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
          {payOpts.map((opt) => (
            <button key={opt} onClick={() => setForm({ ...form, payment: opt })}
              style={{ background: form.payment === opt ? C.gold + "22" : C.card, border: `1px solid ${form.payment === opt ? C.gold : C.border}`, borderRadius: 10, padding: "12px 14px", color: form.payment === opt ? C.gold : C.muted, fontSize: 13, cursor: "pointer", fontFamily: "inherit", textAlign: "left", fontWeight: form.payment === opt ? "bold" : "normal" }}>
              {form.payment === opt ? "✓ " : ""}{opt}
            </button>
          ))}
        </div>

        {form.payment === "Bank Transfer (Zenith Bank)" && (
          <div style={{ background: C.card, border: `1px solid ${C.gold}33`, borderRadius: 12, padding: "14px", marginBottom: 16 }}>
            <div style={{ fontSize: 10, letterSpacing: 2, color: C.gold, marginBottom: 8 }}>BANK DETAILS</div>
            <div style={{ fontSize: 13, color: C.cream, lineHeight: 2 }}>{BANK_DETAILS}</div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>Send payment receipt via WhatsApp after booking</div>
          </div>
        )}

        <label style={lbl}>SPECIAL REQUESTS (OPTIONAL)</label>
        <textarea style={{ ...inp, minHeight: 80, resize: "vertical" }} placeholder="Early check-in, extra pillows, ground floor preference..."
          value={form.special} onChange={(e) => setForm({ ...form, special: e.target.value })} />

        {/* Booking summary */}
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: "16px", marginBottom: 4 }}>
          <div style={{ fontSize: 10, letterSpacing: 3, color: C.gold, marginBottom: 12 }}>BOOKING SUMMARY</div>
          {[
            ["Room", selectedRoom?.name],
            ["Check-in", checkin ? new Date(checkin).toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" }) : ""],
            ["Check-out", checkout ? new Date(checkout).toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" }) : ""],
            ["Nights", nights],
            ["Guests", guests],
            ["Breakfast", breakfast ? `Yes — ${fmt(bkfstTotal)}` : "Not included"],
          ].map(([k, v]) => (
            <div key={k} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 7 }}>
              <span style={{ color: C.muted }}>{k}</span>
              <span style={{ color: C.cream, fontWeight: "500" }}>{v}</span>
            </div>
          ))}
          <div style={{ display: "flex", justifyContent: "space-between", borderTop: `1px solid ${C.border}`, paddingTop: 10, marginTop: 4 }}>
            <span style={{ fontWeight: "bold", fontSize: 15 }}>Total to Pay</span>
            <span style={{ fontWeight: "bold", fontSize: 20, color: C.gold }}>{fmt(grandTotal)}</span>
          </div>
        </div>

        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: "16px 18px", margin: "14px 0" }}>
          <div style={{ fontSize: 10, letterSpacing: 3, color: C.gold, marginBottom: 12 }}>BOOKING POLICY</div>
          {[
            ["💳", "Full payment required to secure your room"],
            ["🏦", "Pay via POS on arrival or bank transfer (Zenith Bank)"],
            ["🔄", "Full refund if cancelled within 48 hours of booking"],
            ["📲", "Reception will confirm your booking via WhatsApp once payment is received"],
          ].map(([icon, text]) => (
            <div key={text} style={{ display: "flex", gap: 10, marginBottom: 10, alignItems: "flex-start" }}>
              <span style={{ fontSize: 16, flexShrink: 0 }}>{icon}</span>
              <span style={{ fontSize: 12, color: C.muted, lineHeight: 1.6 }}>{text}</span>
            </div>
          ))}
        </div>

        <button onClick={() => {
          if (!form.name || !form.phone || !form.payment) return alert("Please fill in your name, phone number and select a payment method.");
          sendBooking();
        }}
          style={{ display: "block", width: "100%", background: sending ? "#1a8a40" : "#25D366", border: "none", borderRadius: 12, padding: "16px", fontSize: 15, fontWeight: "bold", color: "#fff", cursor: "pointer", letterSpacing: 0.5, fontFamily: "inherit" }}>
          {sending ? "⏳ Opening WhatsApp..." : `📲 Confirm Booking via WhatsApp · ${fmt(grandTotal)}`}
        </button>
        <p style={{ textAlign: "center", fontSize: 10, color: C.muted, marginTop: 10, letterSpacing: 1 }}>
          Your booking goes directly to reception on WhatsApp
        </p>
      </div>
    );
  };

  // ── CONFIRM ─────────────────────────────────────────────────────────────────
  const renderConfirm = () => (
    <div style={{ padding: 32, textAlign: "center" }}>
      <div style={{ fontSize: 64, marginBottom: 12 }}>🏨</div>
      <div style={{ fontSize: 28, fontWeight: "bold", color: C.gold, marginBottom: 8 }}>Booking Sent!</div>
      <div style={{ fontSize: 11, color: C.muted, letterSpacing: 3, marginBottom: 12 }}>BOOKING REFERENCE</div>
      <div style={{ background: C.card, border: `1px solid ${C.gold}44`, borderRadius: 14, padding: "14px 28px", fontSize: 22, fontWeight: "bold", color: C.gold, letterSpacing: 4, display: "inline-block", marginBottom: 20 }}>{bookingRef}</div>

      <div style={{ background: "#25D36618", border: "1px solid #25D36644", borderRadius: 14, padding: "16px 18px", marginBottom: 20 }}>
        <div style={{ fontSize: 22, marginBottom: 6 }}>📲</div>
        <div style={{ color: "#25D366", fontWeight: "bold", fontSize: 14, marginBottom: 6 }}>Booking request sent to reception</div>
        <div style={{ color: C.muted, fontSize: 12, lineHeight: 1.6 }}>
          Our team will confirm your booking once payment is received. Please save your booking reference above.
        </div>
      </div>

      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 18, textAlign: "left", marginBottom: 20 }}>
        <div style={{ fontSize: 10, letterSpacing: 3, color: C.gold, marginBottom: 12 }}>WHAT HAPPENS NEXT</div>
        {[
          ["1️⃣", "Make your payment via your chosen method"],
          ["2️⃣", "Send payment receipt to reception on WhatsApp"],
          ["3️⃣", "Reception confirms your booking"],
          ["4️⃣", "Arrive and enjoy your stay at The THE 401 HOTEL & SUITES LTD"],
        ].map(([num, step]) => (
          <div key={num} style={{ display: "flex", gap: 10, marginBottom: 10, fontSize: 13 }}>
            <span>{num}</span>
            <span style={{ color: C.muted }}>{step}</span>
          </div>
        ))}
      </div>

      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "12px 16px", marginBottom: 20, textAlign: "left" }}>
        <div style={{ fontSize: 10, letterSpacing: 2, color: C.gold, marginBottom: 8 }}>BANK DETAILS</div>
        <div style={{ fontSize: 13, color: C.cream, lineHeight: 1.8 }}>{BANK_DETAILS}</div>
      </div>

      <button onClick={() => { setPage("home"); setSelectedRoom(null); setCheckin(""); setCheckout(""); setGuests(1); setBreakfast(false); setForm({ name: "", phone: "", email: "", payment: "", special: "" }); }}
        style={{ background: "transparent", border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 24px", color: C.muted, cursor: "pointer", fontFamily: "inherit", fontSize: 13 }}>
        ← Make Another Booking
      </button>
    </div>
  );

  const backDest = { rooms: "home", detail: "rooms", booking: "detail" };

  return (
    <div style={{ fontFamily: "'Palatino Linotype','Book Antiqua',Palatino,Georgia,serif", background: C.bg, minHeight: "100vh", color: C.cream, maxWidth: "100%", margin: "0 auto" }}>
      <NavBar title="Room Booking" />
      <style>{`@media(min-width:768px){.pb{max-width:720px;margin:0 auto}.cr{display:grid!important;grid-template-columns:repeat(auto-fill,minmax(170px,1fr))!important}}`}</style>
      {/* Header */}
      <div style={{ background: C.surface, borderBottom: `1px solid ${C.border}`, padding: "14px 18px", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100 }}>
        <div>
          <img src={LOGO} style={{height:38,objectFit:"contain",display:"block"}} alt="The 401 Hotel & Suites Ltd" />
          <div style={{ fontSize: 10, color: C.muted, letterSpacing: 2 }}>ROOM BOOKING</div>
        </div>
        {page !== "home" && page !== "confirm" && (
          <button onClick={() => setPage(backDest[page] || "home")}
            style={{ background: "transparent", border: `1px solid ${C.border}`, borderRadius: 8, padding: "7px 12px", color: C.muted, cursor: "pointer", fontSize: 12, fontFamily: "inherit" }}>← Back</button>
        )}
      </div>

      {/* Progress bar */}
      {["rooms", "detail", "booking", "confirm"].includes(page) && (
        <div style={{ display: "flex", background: C.surface, borderBottom: `1px solid ${C.border}` }}>
          {[["rooms", "1. Rooms"], ["detail", "2. Dates"], ["booking", "3. Details"], ["confirm", "4. Confirm"]].map(([p, label]) => {
            const steps = ["rooms", "detail", "booking", "confirm"];
            const current = steps.indexOf(page);
            const thisStep = steps.indexOf(p);
            const active = thisStep <= current;
            return (
              <div key={p} style={{ flex: 1, padding: "8px 4px", textAlign: "center", borderBottom: active ? `2px solid ${C.gold}` : "2px solid transparent" }}>
                <div style={{ fontSize: 9, color: active ? C.gold : C.muted, letterSpacing: 1 }}>{label}</div>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ paddingBottom: 40 }}>
        {page === "home" && renderHome()}
        {page === "rooms" && renderRooms()}
        {page === "detail" && renderDetail()}
        {page === "booking" && renderBooking()}
        {page === "confirm" && renderConfirm()}
      </div>
    </div>
  );
}
